import type { BriefingSourceType } from './Briefing';

/** Native-host-controlled mock mode for the MyOutlook Today OPX surface. */
export const TODAY_OPX_MOCK_MODE = 'mockBriefing' as const;
export type TodayOpxMockMode = typeof TODAY_OPX_MOCK_MODE;

/** Validates the unchecked `getScenarioData` mock-mode value. */
export function normalizeTodayOpxMockMode(value: unknown): TodayOpxMockMode | null {
    return value === TODAY_OPX_MOCK_MODE ? value : null;
}

/**
 * Interaction capability tokens an embedding host advertises for the MyOutlook
 * Today surface. Each token names a host interaction Today may safely surface.
 *
 * Sources per host:
 * - **OPX** (Win32 / Mac Outlook) reports its set through `getScenarioData`.
 * - **MetaOS** publishes the source opens it supports, including browser fallbacks.
 *
 * The existing OPX-named API is retained for compatibility across both hosts.
 *
 * This replaces a monolithic version number: client-web and the hosts ship on
 * independent trains, so a capability set lets a host opt into interactions as
 * it implements them instead of forcing lock-step version bumps.
 *
 * There are five flavors of token:
 *
 * - **Source-open** tokens (`openMail`, `openEvent`, `openTeamsChat`,
 *   `openLink`) gate the primary "open the source" affordance. An item is only
 *   rendered when the host reported the token its `sourceType` maps to (see
 *   {@link isBriefingSourceTypeSupported}).
 * - **Affordance** tokens (`openDraftReply`, `openRecap`, `openFeedback`,
 *   `scheduleMeeting`) gate buttons that require host support. The surrounding
 *   content still renders; the button is only shown when the host reported the
 *   matching token (see {@link hasTodayOpxCapability}).
 * - **Reporting** tokens (`reportRenderResult`) gate optional client-to-host
 *   messages. They are never assumed for legacy hosts.
 * - The **Settings** token (`openMyOutlookSettings`) gates native My Outlook
 *   settings navigation. The host owns account selection and visible navigation
 *   failures.
 * - **Lifecycle** tokens are opt-in: `reloadTodayDocument` promises that the Today
 *   document may navigate and reinitialize its bridge without restarting the host or
 *   losing account/auth context. The host supplies `lifecycle.reloadBlocked` initially
 *   and through scenario-data updates to protect native-owned work. Missing or invalid
 *   safety is blocked. `todayVisibility` promises reliable visibility-change events.
 *   Neither token is inferred from the OS, OPX support, or source-open capabilities.
 *
 * Naming mirrors the outbound `onOpenTarget` "open" contract (host-interaction
 * casing, e.g. `openMail`). Matching is case-insensitive — see
 * {@link normalizeTodayOpxCapabilities}. This union is the single source of
 * truth for the token names; hosts reference these string literals directly.
 */
export type TodayOpxCapability =
    | 'openMail'
    | 'openEvent'
    | 'openTeamsChat'
    | 'openLink'
    | 'reportRenderResult'
    | 'openMyOutlookSettings'
    | 'reloadTodayDocument'
    | 'todayVisibility'
    | TodayOpxAffordanceCapability;

/**
 * The subset of {@link TodayOpxCapability} tokens that gate a secondary per-item
 * affordance (a button/chip) rather than whether an item renders. These are
 * opt-in and are not part of {@link BASELINE_TODAY_OPX_CAPABILITIES}.
 */
export type TodayOpxAffordanceCapability =
    | 'openDraftReply'
    | 'openRecap'
    | 'openBriefing'
    | 'openFeedback'
    | 'reviewAgenda'
    | 'scheduleMeeting';

/**
 * Baseline capability set assumed when the host reports nothing usable — a
 * legacy host, `scenarioData` disabled, an empty payload, or a payload whose
 * tokens are all unrecognized. Equals only the source-open tokens, so a silent
 * host still shows every briefing item type (today's behavior) rather than a
 * blank page.
 *
 * Affordance tokens are intentionally *not* baseline: they gate buttons that
 * require newer host click contracts, so a host must opt into each one
 * explicitly. Inside an embedding host a missing affordance token hides the
 * button; the standalone full page is unaffected because affordance gating only
 * applies within an embedding host (see `isTodayOpxAffordanceVisible`).
 * Reporting and settings tokens are also opt-in because generic scenario-data
 * support does not prove that a host understands those contracts.
 */
export const BASELINE_TODAY_OPX_CAPABILITIES: readonly TodayOpxCapability[] = [
    'openMail',
    'openEvent',
    'openTeamsChat',
    'openLink',
];

/**
 * Maps each source-open capability token to the single `BriefingSourceType` it
 * gates. Affordance tokens are intentionally absent — they gate a button, not
 * whether the item renders. An actionable item is shown only when the host
 * reported the capability its source type maps to.
 */
const SOURCE_TYPE_BY_CAPABILITY: Partial<Record<TodayOpxCapability, BriefingSourceType>> = {
    openMail: 'email',
    openEvent: 'calendar',
    openTeamsChat: 'teams',
    openLink: 'link',
};

/**
 * Case-insensitive lookup from a lower-cased token to its canonical form. Lets
 * a host advertise `openmail`, `OpenMail`, etc. and still match `openMail`.
 */
const CANONICAL_BY_LOWERCASE: Record<string, TodayOpxCapability> = {
    openmail: 'openMail',
    openevent: 'openEvent',
    openteamschat: 'openTeamsChat',
    openlink: 'openLink',
    opendraftreply: 'openDraftReply',
    openrecap: 'openRecap',
    openbriefing: 'openBriefing',
    reportrenderresult: 'reportRenderResult',
    openfeedback: 'openFeedback',
    schedulemeeting: 'scheduleMeeting',
    openmyoutlooksettings: 'openMyOutlookSettings',
    reloadtodaydocument: 'reloadTodayDocument',
    todayvisibility: 'todayVisibility',
};

/**
 * Normalizes a raw, host-supplied capability list into a de-duplicated set of
 * canonical {@link TodayOpxCapability} tokens.
 *
 * - Matching is case-insensitive and whitespace-trimmed.
 * - Unknown tokens are ignored (forward compatibility for future capabilities).
 * - The input comes from `getScenarioData`, an unchecked cast over untrusted
 *   host data. A non-array (or absent / empty) value falls back to
 *   {@link BASELINE_TODAY_OPX_CAPABILITIES} unless the caller requests an
 *   explicit allow-list.
 * - Source-open coverage is never stripped: if the host advertised no
 *   recognized source-open token (an all-unknown or affordance-only payload),
 *   the baseline source-open set is restored when fallback is enabled. Any
 *   affordance tokens the host opted into are preserved.
 */
export function normalizeTodayOpxCapabilities(
    raw: readonly string[] | null | undefined,
    useBaselineFallback: boolean = true
): TodayOpxCapability[] {
    if (!Array.isArray(raw) || raw.length === 0) {
        return useBaselineFallback ? [...BASELINE_TODAY_OPX_CAPABILITIES] : [];
    }

    const matched = new Set<TodayOpxCapability>();
    for (const token of raw) {
        const canonical = CANONICAL_BY_LOWERCASE[String(token).trim().toLowerCase()];
        if (canonical) {
            matched.add(canonical);
        }
    }

    const hasSourceOpen = [...matched].some(
        capability => SOURCE_TYPE_BY_CAPABILITY[capability] !== undefined
    );
    if (!hasSourceOpen && useBaselineFallback) {
        for (const capability of BASELINE_TODAY_OPX_CAPABILITIES) {
            matched.add(capability);
        }
    }

    return [...matched];
}

/**
 * Whether a specific capability token is present in the host's reported set.
 */
export function hasTodayOpxCapability(
    capability: TodayOpxCapability,
    capabilities: readonly TodayOpxCapability[]
): boolean {
    return capabilities.indexOf(capability) !== -1;
}
